import React, { FC, useEffect, useMemo, useState } from 'react'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import { useSetRecoilState } from 'recoil'
import { Calendar, Event } from '../types'
import { postHomeownerRescheduleRequest } from '../api'
import UpdateAvailability from './UpdateAvailability'
import RescheduleReason from './RescheduleReason'
import { homeownerSiteVisitCalendarState } from '../state'
import { Block } from '../Availabilities/Time'

dayjs.extend(utc)
dayjs.extend(timezone)

type RescheduleProps = {
  calendar: Calendar
  event: Event
  setRescheduling: (rescheduling: boolean) => void
  setReschedulingRequestSent: (reschedulingRequestSent: boolean) => void
}

const Reschedule: FC<RescheduleProps> = ({ calendar, event, setRescheduling, setReschedulingRequestSent }) => {
  const setCalendar = useSetRecoilState(homeownerSiteVisitCalendarState)
  const [gotReason, setGotReason] = useState(false)
  const [reason, setReason] = useState('')
  const [sendingRescheduleRequest, setSendingRescheduleRequest] = useState(false)

  // Increment size will be 30m, or if our event duration is smaller, that.
  const incrementSize = useMemo(() => Math.min(30, calendar.event_duration) * 60 * 1000, [calendar])

  const adjustedNow = useMemo(() => {
    return new Date(
      Math.ceil(
        dayjs()
          .tz(calendar.property.time_zone)
          .add(calendar.booking_lead_time || 0, 'minutes')
          .toDate()
          .getTime() / incrementSize
      ) * incrementSize
    )
  }, [calendar, incrementSize])

  const [availabilities, setAvailabilities] = useState<Block[]>(() => {
    // Normalize the start and end times.
    const blocks = calendar.availabilities.reduce((newAvail, block) => {
      const origStart = dayjs(block.time.start).tz(calendar.property.time_zone).toDate()
      const origEnd = dayjs(block.time.end).tz(calendar.property.time_zone).toDate()

      // Show nothing before 'now' (plus lead time)
      const start = Math.max(adjustedNow.getTime(), Math.floor(origStart.getTime() / incrementSize) * incrementSize)
      const end = Math.ceil(origEnd.getTime() / incrementSize) * incrementSize

      // Only need to add if the timespan is > 0
      if (start < end) {
        newAvail.push({
          start: dayjs(start).tz(calendar.property.time_zone).toDate(),
          end: dayjs(end).tz(calendar.property.time_zone).toDate(),
        })
      }
      return newAvail
    }, [] as Block[])

    return blocks
  })

  useEffect(() => {
    if (!sendingRescheduleRequest) return

    const controller = new AbortController()
    const _ = async () => {
      try {
        const body = { homeowner_site_visit: { reason, availabilities } }
        const response = await postHomeownerRescheduleRequest(calendar.id, event.id, body)
        if (!response.isError) {
          setCalendar(response.jsonBody.calendar)
          setReschedulingRequestSent(true)
          setRescheduling(false)
        } else {
          console.error(response.jsonBody)
          alert('An unexpected error has occurred, please reload the page')
        }
      } catch (e) {
        if (!controller.signal.aborted) {
          console.error(e)
        }
      } finally {
        setSendingRescheduleRequest(false)
      }
    }
    _()

    return () => {
      controller.abort()
    }
  }, [
    availabilities,
    calendar.id,
    event.id,
    reason,
    sendingRescheduleRequest,
    setCalendar,
    setRescheduling,
    setReschedulingRequestSent,
  ])

  if (!gotReason) {
    return <RescheduleReason reason={reason} setReason={setReason} setGotReason={setGotReason} />
  }

  return (
    <UpdateAvailability
      calendar={calendar}
      event={event}
      availabilities={availabilities}
      setAvailabilities={setAvailabilities}
      adjustedNow={adjustedNow}
      sendingRescheduleRequest={sendingRescheduleRequest}
      setSendingRescheduleRequest={setSendingRescheduleRequest}
    />
  )
}

export default Reschedule
